import { Link } from 'react-router-dom';
import {
  MapPin, Sparkles, Users, ShieldCheck, ArrowRight, BookOpen,
} from 'lucide-react';
import { CTAPair } from '@kollez/design-system';
import { PageMeta } from '@/components/PageMeta';

/**
 * /about — timeline + who-we-are format.
 *
 * Every other marketing page has its own shape. /about tells the
 * Kollez origin story as a vertical timeline (year by year, milestone
 * per row), followed by a "who we are" strip with team-role tiles and
 * a values section that pairs commitments to concrete practices.
 *
 * Design spec: docs/design/marketing/_per-page-design-language.md
 * (about gets the timeline + who-we-are format).
 */

interface Milestone {
  year: string;
  when: string;
  headline: string;
  body: string;
  tag?: 'founding' | 'product' | 'growth' | 'trust';
}

const MILESTONES: Milestone[] = [
  {
    year: '2023',
    when: 'Winter',
    tag: 'founding',
    headline: 'The scaffold.',
    body: "Sonido Labs was set up in Hyderabad to build software for Indian education. First month: talking to placement officers at five colleges across Andhra Pradesh and Telangana. Every one of them ran placements on WhatsApp groups and shared Excel sheets. Every one of them wanted the same thing — a register.",
  },
  {
    year: '2024',
    when: 'Q1',
    tag: 'product',
    headline: 'The first register.',
    body: 'The MVP had exactly one page: a placement register. Post a drive, screen applicants, release offers. Two engineers, one design pass, one placement head in the loop for every decision. Shipped to Godavari Institute of Technology as the first pilot.',
  },
  {
    year: '2024',
    when: 'Q3',
    tag: 'product',
    headline: 'Portable badges.',
    body: 'A student took an aptitude test as part of one drive. Two weeks later they applied to another opportunity whose workflow began with the same aptitude test. Retaking it was wasteful, unfair, lost signal. We designed Badges — outcomes owned by the student, follow them across every future Application. That was the moment Kollez became a platform, not just a tool.',
  },
  {
    year: '2025',
    when: 'Q1',
    tag: 'trust',
    headline: 'Ethics-in-the-open.',
    body: 'Wrote the three rules that would gate every partner: never charge students, real offerings only, no PII harvesting. Verified partners had to sign; unverified listings were removed. Some partners walked. Most did not.',
  },
  {
    year: '2025',
    when: 'Q3',
    tag: 'product',
    headline: 'Nexie.',
    body: 'Added an AI layer that is grounded in tenant data, cites every claim, and never takes an autonomous action. Career Advisor for students, Auto-shortlist for partners, Group Insights for institutions. Every action requires a human sign-off. Every acceptance has a 10-minute undo window.',
  },
  {
    year: '2026',
    when: 'Q1',
    tag: 'growth',
    headline: 'Malla Reddy Group live.',
    body: 'Five colleges as one operation, one register per college, one roll-up for the group at 8 am. 87 drives that season, up from 34. First multi-college customer signed off on the metrics you see on the /customers page.',
  },
  {
    year: '2026',
    when: 'Q3',
    tag: 'trust',
    headline: 'DPDP Act 2023 ready.',
    body: 'Consent flows captured with a full audit trail. Students can export or delete anything, anytime. Contact hidden until shortlist. 60-day soft-delete window. Data stays in India, always.',
  },
  {
    year: 'Today',
    when: '2026-2027 season',
    tag: 'growth',
    headline: 'The register, the AI, and the badges.',
    body: 'Where we are as of the current placement season. Six modes of assessment, twelve question types, four issuer tiers on badges, one AI, three sides — students, partners, institutions. Everything grounded, everything cited, everything with human sign-off. Kept honest by the placement heads who use it every morning.',
  },
];

interface Role {
  team: string;
  count: string;
  focus: string;
  initials: string;
}

const TEAM: Role[] = [
  { team: 'Engineering',       count: '8', focus: 'Full-stack (React + Node + Mongo), assessment engine, Nexie grounding',    initials: 'E' },
  { team: 'Design',            count: '2', focus: 'Ledger identity, marketing surface, and the primitive library',            initials: 'D' },
  { team: 'AI / Nexie',        count: '3', focus: 'Retrieval + rubric + integrity for the AI layer',                          initials: 'N' },
  { team: 'Customer success',  count: '4', focus: 'Placement pilots, group rollouts, weekly TPO office hours',                initials: 'C' },
  { team: 'Compliance',        count: '2', focus: 'DPDP, SOC 2, ISO 27001, DPA responses',                                    initials: 'K' },
  { team: 'Founders',          count: '2', focus: 'One placement head background, one platform-engineering background',        initials: 'F' },
];

interface Value {
  title: string;
  practice: string;
}

const VALUES: Value[] = [
  { title: 'Honest with students',
    practice: 'Students never pay on Kollez. Not for sign-up, tests, mock interviews, offers, or anything else. If anyone asks — tell us and we act within a day.' },
  { title: 'Grounded, not generated',
    practice: 'Every Nexie answer cites the row it drew from. If Nexie can\'t find a source, it says "I don\'t know" instead of making something up.' },
  { title: 'Human sign-off, always',
    practice: 'No AI-authored action ships without a person on your team approving it. Every acceptance has a 10-minute undo window.' },
  { title: 'Cross-tenant privacy',
    practice: 'Your students\' data does not train a model shared with any other college or partner. Ever.' },
  { title: 'Audit before opinion',
    practice: 'Every mutation is audit-logged. When something goes wrong, you can see exactly what happened, when, by whom.' },
  { title: 'Made in India, for India',
    practice: 'NIRF / NAAC / AICTE formats native. INR-native. +91 mobile-first. Data hosted in India. Support in your working hours.' },
];

/* ────────── sub-components ────────── */

function TagChip({ tag }: { tag: NonNullable<Milestone['tag']> }) {
  const styles: Record<NonNullable<Milestone['tag']>, { bg: string; fg: string; label: string }> = {
    founding: { bg: 'rgb(var(--bg-info))',     fg: 'rgb(var(--ink-accent))',   label: 'Founding' },
    product:  { bg: 'rgb(var(--bg-success))',  fg: 'rgb(var(--ink-success))',  label: 'Product' },
    growth:   { bg: 'rgb(var(--bg-warning))',  fg: 'rgb(var(--ink-warning))',  label: 'Growth'  },
    trust:    { bg: 'rgb(var(--bg-secondary))',fg: 'rgb(var(--ink-secondary))',label: 'Trust'   },
  };
  const s = styles[tag];
  return (
    <span
      className="text-[10px] font-medium uppercase tracking-wider rounded-full px-2 py-0.5"
      style={{ background: s.bg, color: s.fg }}
    >
      {s.label}
    </span>
  );
}

function TimelineRow({ m, isLast }: { m: Milestone; isLast: boolean }) {
  return (
    <li className="grid gap-4 grid-cols-[64px_1fr] sm:grid-cols-[100px_1fr] lg:grid-cols-[120px_1fr] relative">
      {/* Year column */}
      <div className="text-right pr-2 pt-1">
        <div
          className="font-serif tabular-nums"
          style={{ fontSize: '1.5rem', fontWeight: 500, lineHeight: 1, color: 'rgb(var(--ink-primary))' }}
        >
          {m.year}
        </div>
        <div className="text-[11px] mt-1" style={{ color: 'rgb(var(--ink-tertiary))' }}>{m.when}</div>
      </div>

      {/* Node + line + content */}
      <div className="grid grid-cols-[24px_1fr] gap-4 relative">
        {/* Node column */}
        <div className="relative">
          <span
            className="absolute top-2 left-1/2 -translate-x-1/2 flex items-center justify-center rounded-full"
            style={{
              width: 14, height: 14,
              background: 'rgb(var(--ink-accent))',
              boxShadow: '0 0 0 4px rgb(var(--bg-primary))',
            }}
          />
          {!isLast && (
            <span
              className="absolute left-1/2 -translate-x-1/2 top-6 bottom-[-32px] w-px"
              style={{ background: 'rgb(var(--line-tertiary))' }}
              aria-hidden="true"
            />
          )}
        </div>

        {/* Content */}
        <div className="pb-2">
          <div className="flex items-baseline gap-2 flex-wrap">
            <h3 className="font-serif text-lg lg:text-xl m-0" style={{ lineHeight: 1.25 }}>
              {m.headline}
            </h3>
            {m.tag && <TagChip tag={m.tag} />}
          </div>
          <p className="mt-2 text-ink-secondary m-0" style={{ maxWidth: '58ch' }}>{m.body}</p>
        </div>
      </div>
    </li>
  );
}

function TeamTile({ role }: { role: Role }) {
  return (
    <div className="card card-pad grid gap-2">
      <div className="flex items-center gap-3">
        <span
          className="flex items-center justify-center rounded-lg font-serif font-semibold"
          style={{
            width: 36, height: 36,
            background: 'rgb(var(--bg-info))',
            color: 'rgb(var(--ink-accent))',
            fontSize: 14,
          }}
        >
          {role.initials}
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium">{role.team}</div>
          <div className="text-[11px]" style={{ color: 'rgb(var(--ink-tertiary))' }}>
            {role.count} {role.count === '1' ? 'person' : 'people'}
          </div>
        </div>
      </div>
      <p className="helper m-0">{role.focus}</p>
    </div>
  );
}

function ValueRow({ v, i }: { v: Value; i: number }) {
  return (
    <li
      className="grid gap-1 py-4 border-t"
      style={{ borderColor: 'rgb(var(--line-tertiary))' }}
    >
      <div className="flex items-baseline gap-3">
        <span
          className="font-serif tabular-nums"
          style={{ fontSize: '1rem', color: 'rgb(var(--ink-tertiary))', minWidth: 24 }}
        >
          {String(i + 1).padStart(2, '0')}
        </span>
        <h3 className="font-serif text-lg m-0" style={{ letterSpacing: '-0.005em' }}>{v.title}</h3>
      </div>
      <p className="text-ink-secondary m-0 ml-9" style={{ maxWidth: '68ch' }}>{v.practice}</p>
    </li>
  );
}

/* ────────── page ────────── */

export function AboutPage() {
  return (
    <>
      <PageMeta
        title="About Kollez"
        description="Kollez is one platform for education, built by Sonido Labs in Hyderabad. Read our origin story on a timeline, meet the team, and see the six values that gate every decision."
        canonical="/about"
      />

      {/* HERO */}
      <section className="mx-auto max-w-page-narrow px-5 lg:px-10 pt-16 lg:pt-24 pb-8 grid gap-6">
        <p className="eyebrow flex items-center gap-1.5">
          <Sparkles size={12} />
          About Kollez
        </p>
        <h1
          className="text-ink-primary"
          style={{
            fontFamily: 'Newsreader, Georgia, serif',
            fontSize: 'clamp(2.25rem, 5vw, 3.5rem)',
            lineHeight: 1.05,
            fontWeight: 500,
            letterSpacing: '-0.01em',
            margin: 0,
          }}
        >
          Built in India,<br />
          for the register India runs on.
        </h1>
        <p className="text-lg leading-8 text-ink-secondary m-0">
          Kollez is the register your placement cell, your recruiter, and your
          student can all read. Built by <b>Sonido Labs</b> in Hyderabad —
          because Indian education needed a system that treats students,
          partners, and institutions as three sides of the same page.
        </p>
        <p
          className="helper flex items-center gap-1.5 m-0"
          style={{ color: 'rgb(var(--ink-tertiary))' }}
        >
          <MapPin size={12} />
          Hyderabad, Telangana · Data stays in India · DPDP Act 2023 ready
        </p>
      </section>

      {/* ORIGIN TIMELINE */}
      <section className="mx-auto max-w-page-wide px-5 lg:px-10 py-16 lg:py-20 grid gap-8">
        <header className="max-w-2xl">
          <p className="eyebrow m-0 text-xs flex items-center gap-1.5">
            <BookOpen size={12} />
            The story so far
          </p>
          <h2 className="h-serif text-3xl lg:text-4xl mt-2 m-0">
            Every register has a first entry.
          </h2>
          <p className="mt-3 text-ink-secondary m-0">
            Kollez started as one page for one placement cell. Every milestone
            below was driven by a real conversation with a real placement head.
            No pivots to another domain, no aggregator ambitions — one platform,
            three sides, kept honest by the people who use it every morning.
          </p>
        </header>
        <ol className="grid gap-8 lg:gap-10 mt-4">
          {MILESTONES.map((m, i) => (
            <TimelineRow key={i} m={m} isLast={i === MILESTONES.length - 1} />
          ))}
        </ol>
      </section>

      {/* WHO WE ARE */}
      <section className="border-t border-line-tertiary bg-primary py-16 lg:py-20">
        <div className="mx-auto max-w-page-wide px-5 lg:px-10 grid gap-8">
          <header className="max-w-2xl">
            <p className="eyebrow m-0 text-xs flex items-center gap-1.5">
              <Users size={12} />
              Who we are
            </p>
            <h2 className="h-serif text-3xl lg:text-4xl mt-2 m-0">
              Twenty-one people, six teams, one register.
            </h2>
            <p className="mt-3 text-ink-secondary m-0">
              Sonido Labs is a small team by design — we ship in weeks, we
              answer support directly, we know every customer. No photos here
              yet; every face gets added as they say yes.
            </p>
          </header>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {TEAM.map((r) => <TeamTile key={r.team} role={r} />)}
          </div>
        </div>
      </section>

      {/* VALUES — numbered list */}
      <section className="mx-auto max-w-page-narrow px-5 lg:px-10 py-16 lg:py-20 grid gap-6">
        <header className="max-w-2xl">
          <p className="eyebrow m-0 text-xs flex items-center gap-1.5">
            <ShieldCheck size={12} />
            What gates every decision
          </p>
          <h2 className="h-serif text-3xl lg:text-4xl mt-2 m-0">
            Six commitments, in plain English.
          </h2>
          <p className="mt-3 text-ink-secondary m-0">
            Every one paired with the concrete practice it produces — because a
            value that doesn&apos;t change how the product works isn&apos;t a
            value, it&apos;s a slide.
          </p>
        </header>
        <ol className="mt-4">
          {VALUES.map((v, i) => (
            <ValueRow key={v.title} v={v} i={i} />
          ))}
        </ol>
      </section>

      {/* MADE-BY CARD + PRESS */}
      <section className="border-t border-line-tertiary bg-primary py-16 lg:py-20">
        <div className="mx-auto max-w-page-narrow px-5 lg:px-10 grid gap-6">
          <div className="card card-pad grid gap-3">
            <p className="micro" style={{ color: 'rgb(var(--ink-tertiary))' }}>Made by</p>
            <h3 className="font-serif text-2xl m-0">Sonido Labs Pvt. Ltd.</h3>
            <p className="text-ink-secondary m-0">
              An Indian software company based in Hyderabad. We build tools
              that make education more transparent, less brittle, and easier
              to trust — for the people who run it and the students who use it.
            </p>
            <p className="helper flex items-center gap-1.5 m-0">
              <MapPin size={12} />
              Hyderabad, Telangana · CIN available on request
            </p>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
            <p className="helper m-0">
              Press or partnership enquiries?{' '}
              <Link className="link" to="/contact?topic=press">Get in touch</Link>.
            </p>
            <Link to="/customers" className="link text-sm inline-flex items-center gap-1">
              Read the customer stories <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="mx-auto max-w-page-wide px-5 py-16 lg:px-10 lg:py-20 text-center">
        <h2 className="display" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)' }}>
          The next milestone is your first drive.
        </h2>
        <p className="mt-4 text-ink-secondary max-w-xl mx-auto">
          Free pilot for one college, one season. Nothing charged today.
        </p>
        <div className="mt-8 flex justify-center">
          <CTAPair
            primary={{ label: 'Book a 30-min demo', to: '/contact?topic=demo' }}
            secondary={{ label: 'Start a free pilot', to: '/register/institution?plan=pilot' }}
            align="center"
          />
        </div>
      </section>
    </>
  );
}
